import { POSTED_PRICE_SET } from '@syncanix/shared';

/**
 * The single worked example published on `/docs/billing/usage` — DERIVED from
 * the pricing SOT, never written into prose.
 *
 * **Why it is a module and not a sentence.** A prospect sizes their plan on this
 * one number, and the prose-authored version was wrong twice over in all six
 * locales: it stated a 6-UC-per-interaction factor and then printed 5,000 x 6 as
 * 20,000, and — the expensive error — it counted a whole support CHAT as a
 * single interaction. A chat runs several turns and every turn is charged
 * separately, so a reader sizing on that sentence under-bought by roughly 6x and
 * would have hit the default hard-stop about a quarter of the way through their
 * month.
 *
 * Deriving the numbers here and interpolating them into the copy means no
 * catalog holds a pricing figure at all, so no locale can carry a stale or
 * miscomputed one. Same pattern as the widget's single-sourced public bundle
 * claim: publish the number from one place, enforce it from both ends.
 *
 * **Under posted pricing (ADR-0442) the example is a RANGE, and an exact one.**
 * There is no typical interaction weight to multiply by any more: an interaction
 * costs one of exactly four posted prices, so the same volume of interactions
 * costs between the base rung applied to all of them and the top rung applied to
 * all of them. Publishing one midpoint would re-introduce the "at a typical
 * weight" hedge the model exists to remove; publishing both ends is exact, and a
 * reader's real month provably lands inside it.
 *
 * Pure data — no I/O, no React, no clock.
 */

/**
 * Charged turns in one support conversation.
 *
 * A conversation is NOT a billing unit; it is a container for several. Published
 * conversational-AI benchmarks put a support session at roughly four user
 * inquiries, each drawing its own assistant turn — so four is the conservative,
 * defensible multiplier between "conversations" (how a buyer thinks about their
 * support volume) and "interactions" (what Syncanix actually charges for).
 * Raising it makes the published example MORE expensive, never less, so an error
 * here fails in the direction that does not oversell capacity.
 */
export const TURNS_PER_CONVERSATION = 4;

/** The conversation volume the worked example is anchored on. */
export const EXAMPLE_CONVERSATIONS_PER_MONTH = 1_000;

/** Charged interactions those conversations produce. */
export const EXAMPLE_INTERACTIONS_PER_MONTH =
  EXAMPLE_CONVERSATIONS_PER_MONTH * TURNS_PER_CONVERSATION;

/**
 * The cheapest those interactions can be: every one a plain answer, at the base
 * rung of the posted menu.
 */
export const EXAMPLE_SIMPLE_USAGE_CREDITS_PER_MONTH =
  EXAMPLE_INTERACTIONS_PER_MONTH * Math.min(...POSTED_PRICE_SET);

/**
 * The dearest they can be: every one both acts and looks something up, at the
 * top of the posted menu. A real month cannot exceed this, which is what makes
 * the published range a ceiling rather than a guess.
 */
export const EXAMPLE_FULL_USAGE_CREDITS_PER_MONTH =
  EXAMPLE_INTERACTIONS_PER_MONTH * Math.max(...POSTED_PRICE_SET);
